import IMask from "imask"
import { parsePhoneNumber } from "libphonenumber-js"

import ErrorHandlerService from "@/services/ErrorHandler"
import { regexList } from "@/utils/regex"

const MASKED_VALUE_CHARACTER = "*"

export const isMaskedValue = (value: string) => {
	return value.includes(MASKED_VALUE_CHARACTER)
}

export const formatBRPhoneNumber = (rawNumber?: string) => {
	let formattedPhoneNumber = rawNumber

	if (rawNumber) {
		const phoneNumberMask = IMask.createMask({
			mask: [
				{
					mask: "+55 (00) 0000-0000"
				},
				{
					mask: "+55 (00) 00000-0000"
				}
			]
		})

		formattedPhoneNumber = phoneNumberMask.resolve(rawNumber)
	}

	return formattedPhoneNumber
}

export const formatPhoneNumber = (rawNumber?: string) => {
	try {
		if (!rawNumber) {
			return rawNumber
		}

		if (isMaskedValue(rawNumber)) {
			return rawNumber
		}

		const parsedPhoneNumber = parsePhoneNumber(`+${rawNumber}`)

		if (!parsedPhoneNumber) {
			return rawNumber
		}

		if (parsedPhoneNumber.country === "BR") {
			return formatBRPhoneNumber(rawNumber)
		} else {
			const internationalFormattedPhoneNumber = parsedPhoneNumber.formatInternational()

			return internationalFormattedPhoneNumber
		}
	} catch (error) {
		ErrorHandlerService.handle(error)
		return rawNumber
	}
}

export const formatUrl = (rawUrl: string) => {
	let formattedUrl: string = rawUrl

	if (rawUrl) {
		const validPrefixes = ["https", "http"]

		// eslint-disable-next-line
		const asterisks = [...new Array(rawUrl.length)].map(_ => "*").join("")

		const defaultPrefix = validPrefixes[0]

		const currentUsedPrefixIndex = validPrefixes.findIndex(prefix => rawUrl.startsWith(prefix))

		let prefix: string

		if (currentUsedPrefixIndex === -1) {
			prefix = defaultPrefix
		} else {
			prefix = validPrefixes[currentUsedPrefixIndex]
		}

		const urlMask = IMask.createMask({
			mask: `${prefix}://${asterisks}`
		})

		formattedUrl = urlMask.resolve(formattedUrl)

		formattedUrl = formattedUrl
			.replace(/\s/g, "")
	}

	return formattedUrl
}

export const formatEmail = (rawEmail: string) => {
	let formattedEmail: string = rawEmail

	if (rawEmail) {
		if (isMaskedValue(rawEmail)) {
			return rawEmail
		}

		formattedEmail = rawEmail
			.replace(/\s/g, "")
	}

	return formattedEmail
}

const VISIBLE_LEADING_PHONE_DIGITS = 0
const VISIBLE_TRAILING_PHONE_DIGITS = 4
const MINIMUM_PHONE_DIGITS_TO_MASK = 5

export const maskEmail = (email: string): string => {
	if (!email) {
		return email
	}

	const atIndex = email.indexOf("@")

	if (atIndex === -1) {
		return email
	}

	const localPart = email.slice(0, atIndex)
	const domain = email.slice(atIndex + 1)

	if (!localPart || !domain) {
		return email
	}

	return `${localPart[0]}***@${domain}`
}

export const maskPhone = (phone: string): string => {
	if (!phone) {
		return phone
	}

	const digitPositions: number[] = []

	for (let index = 0; index < phone.length; index++) {
		if (regexList.is_digit.test(phone[index])) {
			digitPositions.push(index)
		}
	}

	const digitsCount = digitPositions.length

	if (digitsCount < MINIMUM_PHONE_DIGITS_TO_MASK) {
		return phone
	}

	const maskedDigitPositions = new Set<number>()

	for (let digitIndex = VISIBLE_LEADING_PHONE_DIGITS; digitIndex < digitsCount - VISIBLE_TRAILING_PHONE_DIGITS; digitIndex++) {
		maskedDigitPositions.add(digitPositions[digitIndex])
	}

	const maskedCharacters: string[] = []

	for (let index = 0; index < phone.length; index++) {
		if (maskedDigitPositions.has(index)) {
			maskedCharacters.push("*")
		} else {
			maskedCharacters.push(phone[index])
		}
	}

	return maskedCharacters.join("")
}
